/**
 * brand.ts — the shared BrandPreset contract (docs/overlay_design.md + Will's white-label plan).
 *
 * ONE white-label Kombi application, not separate per-brand forks. Every shared component reads its
 * colours, fonts, logo, wordmark and media from the SELECTED BrandPreset. A preset is a server-owned,
 * globally published definition; the existing VW/Kombi look is the DEFAULT preset and the fallback.
 *
 * This module is the contract BOTH tiers build against (Will's integration step 1). The web brand
 * drawer first renders FIXTURES of these types, then swaps in the API's published-preset list without
 * changing its presentation; the API validates writes and serialises reads to these same shapes. A
 * drift between the two is the bug this shared home exists to make impossible.
 *
 * ── THE SPLIT OF CONCERNS THE STORAGE MIRRORS ───────────────────────────────────────────────────
 *  - A preset's SMALL fields (name, wordmark, five colours, status, version) are columns on
 *    app.brand_preset.
 *  - Its BINARIES (logo, drawer thumbnail, fonts, welcome video, animation bundle, per-page images)
 *    are NOT inline. They live in the on-disk blob store (db/blobStore.ts) and are referenced here by
 *    a same-origin URL the web fetches on demand. On a Raspberry Pi only the SELECTED preset's assets
 *    are ever loaded, and each is immutably cacheable (the blob ref is content-addressed), so a heavy
 *    welcome video or animation bundle is fetched once and served from cache thereafter.
 *  - WHICH preset a person has selected is NOT here and NOT on the server at all: it is the web's
 *    localStorage, namespaced by user id (Will owns it). Selecting a preset never calls the server and
 *    never changes a global setting — it only swaps the tokens/assets the components read.
 *
 * ── INJECTION SAFETY BY CONSTRUCTION ────────────────────────────────────────────────────────────
 * Colours must match BRAND_HEX_COLOR_RE (a #hex, nothing else) — the same rule doubles as the guard
 * that a colour can never carry a ';', '}' or 'url(...)' into a <style>. Fonts are UPLOADED files
 * (Will: "font asset references"), served from the blob store and bound to a deterministic
 * `@font-face` family the web names itself — an author-supplied family string never reaches CSS. Asset
 * URLs are same-origin /api/branding/... paths the API mints, never author free-text. The wordmark and
 * name render as escaped text, never as CSS.
 */
/**
 * One themeable asset slot on a preset. The set is CLOSED (the API rejects an unknown slot, the editor
 * offers exactly these) for the same reason the colour rule is a regex: a slot name is part of the
 * agreed contract, not free text. Logo + thumbnail are the drawer's own needs; fonts are uploaded
 * files; the three orientation videos and animation bundle are the heavy Pi-sensitive assets; the image.* slots
 * are the editorial pictures shared components render (the nine tool-catalogue images to begin with —
 * their dotted names mirror packages/web/src/kombi/catalogue/categoryDesign.ts). Adding a slot here is
 * additive and needs no migration (app.brand_preset_asset stores the slot as text).
 */
export const BRAND_ASSET_SLOTS = [
    'logo',
    'thumbnail',
    'font.sans.light',
    'font.sans',
    'font.sans.bold',
    'font.heading.light',
    'font.heading',
    'font.heading.bold',
    'font.mono',
    'font.mono.medium',
    'video.welcome',
    'video.useKombi',
    'video.contribute',
    'video.automation.submitted',
    'image.home.opening',
    'image.automation.submitted',
    'anim.bundle',
    'image.admin.hero',
    'image.catalogue.aiAgents.intro',
    'image.catalogue.aiAgents.wide',
    'image.catalogue.aiAgents.contribution',
    'image.catalogue.apps.intro',
    'image.catalogue.apps.wide',
    'image.catalogue.apps.contribution',
    'image.catalogue.automations.intro',
    'image.catalogue.automations.wide',
    'image.catalogue.automations.contribution',
];
export function isBrandAssetSlot(v) {
    return typeof v === 'string' && BRAND_ASSET_SLOTS.includes(v);
}
export const BRAND_ASSET_SLOT_SPECS = [
    { slot: 'logo', kind: 'image', label: 'Logo', requiredToPublish: false },
    { slot: 'thumbnail', kind: 'image', label: 'Drawer thumbnail', requiredToPublish: false },
    {
        slot: 'font.sans.light',
        kind: 'font',
        label: 'Primary font — Light (300)',
        requiredToPublish: false,
    },
    {
        slot: 'font.sans',
        kind: 'font',
        label: 'Primary font — Regular (400)',
        requiredToPublish: false,
    },
    {
        slot: 'font.sans.bold',
        kind: 'font',
        label: 'Primary font — Bold (700)',
        requiredToPublish: false,
    },
    {
        slot: 'font.heading.light',
        kind: 'font',
        label: 'Heading font — Light (300)',
        requiredToPublish: false,
    },
    {
        slot: 'font.heading',
        kind: 'font',
        label: 'Heading font — Regular (400)',
        requiredToPublish: false,
    },
    {
        slot: 'font.heading.bold',
        kind: 'font',
        label: 'Heading font — Bold (700)',
        requiredToPublish: false,
    },
    {
        slot: 'font.mono',
        kind: 'font',
        label: 'Technical font — Regular (400)',
        requiredToPublish: false,
    },
    {
        slot: 'font.mono.medium',
        kind: 'font',
        label: 'Technical font — Medium (500)',
        requiredToPublish: false,
    },
    { slot: 'video.welcome', kind: 'video', label: 'Welcome video', requiredToPublish: false },
    {
        slot: 'video.useKombi',
        kind: 'video',
        label: 'How to use Kombi video',
        requiredToPublish: false,
    },
    {
        slot: 'video.contribute',
        kind: 'video',
        label: 'How to contribute video',
        requiredToPublish: false,
    },
    {
        slot: 'video.automation.submitted',
        kind: 'video',
        label: 'Automation submission animation',
        requiredToPublish: false,
    },
    {
        slot: 'image.home.opening',
        kind: 'image',
        label: 'Home scroll still image',
        requiredToPublish: false,
    },
    {
        slot: 'image.automation.submitted',
        kind: 'image',
        label: 'Automation submission still image',
        requiredToPublish: false,
    },
    {
        slot: 'anim.bundle',
        kind: 'bundle',
        label: 'Animation bundle (zip)',
        requiredToPublish: false,
    },
    {
        slot: 'image.admin.hero',
        kind: 'image',
        label: 'Admin Tools hero image',
        requiredToPublish: false,
    },
    {
        slot: 'image.catalogue.aiAgents.intro',
        kind: 'image',
        label: 'AI Agents — intro image',
        requiredToPublish: false,
    },
    {
        slot: 'image.catalogue.aiAgents.wide',
        kind: 'image',
        label: 'AI Agents — wide banner',
        requiredToPublish: false,
    },
    {
        slot: 'image.catalogue.aiAgents.contribution',
        kind: 'image',
        label: 'AI Agents — contribution image',
        requiredToPublish: false,
    },
    {
        slot: 'image.catalogue.apps.intro',
        kind: 'image',
        label: 'Apps — intro image',
        requiredToPublish: false,
    },
    {
        slot: 'image.catalogue.apps.wide',
        kind: 'image',
        label: 'Apps — wide banner',
        requiredToPublish: false,
    },
    {
        slot: 'image.catalogue.apps.contribution',
        kind: 'image',
        label: 'Apps — contribution image',
        requiredToPublish: false,
    },
    {
        slot: 'image.catalogue.automations.intro',
        kind: 'image',
        label: 'Automations — intro image',
        requiredToPublish: false,
    },
    {
        slot: 'image.catalogue.automations.wide',
        kind: 'image',
        label: 'Automations — wide banner',
        requiredToPublish: false,
    },
    {
        slot: 'image.catalogue.automations.contribution',
        kind: 'image',
        label: 'Automations — contribution image',
        requiredToPublish: false,
    },
];
/** Look up a slot's spec (kind/label/required). Undefined for an unknown slot. */
export function brandAssetSlotSpec(slot) {
    return BRAND_ASSET_SLOT_SPECS.find((s) => s.slot === slot);
}
/**
 * The upload rules per asset kind — the allowed MIME types and the byte ceiling (Will: "Validate
 * colours, fonts, images, videos and animation bundles" + "clear upload and storage limits"). The caps
 * are deliberately modest for a Raspberry Pi and are the DEFAULTS the upload route enforces; they can
 * be made owner-tunable later the same way max_artefact_bytes is (blobStore takes the cap as an
 * argument, it is not hardcoded there). MIME is cross-checked against the bytes' magic signature at
 * upload — an extension/type claim alone is not a safety control.
 */
export const BRAND_ASSET_KIND_RULES = {
    image: {
        mimes: ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'],
        maxBytes: 2 * 1024 * 1024, // 2 MB — logos, thumbnails, editorial pictures
    },
    font: {
        mimes: ['font/woff2', 'application/font-woff2'],
        maxBytes: 1 * 1024 * 1024, // 1 MB — a single woff2 face
    },
    video: {
        mimes: ['video/mp4', 'video/webm'],
        maxBytes: 20 * 1024 * 1024, // 20 MB — a short welcome clip, streamed + range-served
    },
    bundle: {
        mimes: ['application/zip'],
        maxBytes: 10 * 1024 * 1024, // 10 MB — an animation frame bundle (zip)
    },
};
/** The byte cap for a given slot, resolved via its kind. Unknown slot → 0 (nothing is acceptable). */
export function brandAssetMaxBytes(slot) {
    const spec = brandAssetSlotSpec(slot);
    return spec ? BRAND_ASSET_KIND_RULES[spec.kind].maxBytes : 0;
}
/** The largest a single asset of ANY kind may be — the transport ceiling the upload route pins its
 * raw() body limit to (a per-slot cap is then re-asserted in-handler). Derived, so adding a bigger kind
 * moves it automatically. */
export const BRAND_ASSET_MAX_ANY_BYTES = Math.max(...Object.values(BRAND_ASSET_KIND_RULES).map((r) => r.maxBytes));
/**
 * The ceiling on the TOTAL bytes one preset's assets may occupy (Will: "clear upload and storage
 * limits"; the Pi loads only the selected preset's media, but a runaway preset must still be bounded).
 * Four 20 MB videos, one 10 MB bundle, five 1 MB font faces and up to fourteen 2 MB images can
 * exceed the deliberately conservative 80 MB preset cap. The upload route sums the
 * preset's existing assets (minus the slot being replaced) and rejects an upload that would exceed this.
 */
export const BRAND_PRESET_MAX_TOTAL_BYTES = 80 * 1024 * 1024;
/** True if `mime` is allowed for the given slot's kind. */
export function isAllowedBrandAssetMime(slot, mime) {
    const spec = brandAssetSlotSpec(slot);
    return spec ? BRAND_ASSET_KIND_RULES[spec.kind].mimes.includes(mime) : false;
}
function isPositiveInt(v) {
    return typeof v === 'number' && Number.isInteger(v) && v > 0;
}
function isNonNegativeInt(v) {
    return typeof v === 'number' && Number.isInteger(v) && v >= 0;
}
/**
 * True if `v` is a self-consistent set of bounds: whole pixels, non-empty, and inside the intrinsic
 * frame. The API validates client-measured bounds with this before storing them — the measurement is
 * taken in the uploader's browser, so it is untrusted input like any other request field.
 */
export function isBrandAssetVisibleBounds(v) {
    if (typeof v !== 'object' || v === null)
        return false;
    const b = v;
    return (isPositiveInt(b.intrinsicWidth) &&
        isPositiveInt(b.intrinsicHeight) &&
        isNonNegativeInt(b.left) &&
        isNonNegativeInt(b.top) &&
        isPositiveInt(b.right) &&
        isPositiveInt(b.bottom) &&
        b.left < b.right &&
        b.top < b.bottom &&
        b.right <= b.intrinsicWidth &&
        b.bottom <= b.intrinsicHeight);
}
/**
 * Scale the visible region to fill the frame's constraining axis, then centre it.
 *
 * This deliberately scales the mark UP past the frame's edges when the file carries padding: the
 * padding is what the frame is meant to discard, so honouring it would waste the box and reproduce
 * the very misalignment the bounds exist to remove.
 */
export function brandLogoFrame(bounds) {
    const { intrinsicWidth, intrinsicHeight } = bounds;
    // A fully transparent image has no visible region to centre; frame the whole file instead.
    const empty = bounds.right <= bounds.left || bounds.bottom <= bounds.top;
    const left = empty ? 0 : bounds.left;
    const top = empty ? 0 : bounds.top;
    const right = empty ? intrinsicWidth : bounds.right;
    const bottom = empty ? intrinsicHeight : bounds.bottom;
    const scale = 1 / Math.max(right - left, bottom - top);
    return {
        width: intrinsicWidth * scale,
        height: intrinsicHeight * scale,
        left: 0.5 - ((left + right) / 2) * scale,
        top: 0.5 - ((top + bottom) / 2) * scale,
    };
}
/**
 * A colour must be a plain #hex (3/4/6/8 digits — what a colour input and an alpha picker produce).
 * This is both the validation rule AND the CSS-injection guard: a value that matches cannot contain a
 * ';', '}', whitespace or 'url(...)', so it is safe to write straight into a CSS custom property.
 */
export const BRAND_HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;
export function isBrandHexColor(v) {
    return typeof v === 'string' && BRAND_HEX_COLOR_RE.test(v);
}
/**
 * The value a preset's `buttonText` takes when nobody has overridden it: its PAPER.
 *
 * The label on a primary button has to read against TWO fills — Ink at rest, and Accent on hover,
 * because Kombi's primary button swaps its fill and keeps its label. A curator cannot be expected to
 * solve that by eye, and the old default solved it by hard-coding white, which only works for a light
 * theme: give a preset a light Ink and a dark Paper and `#ffffff` becomes white-on-near-white. Paper is
 * already contracted to contrast with Ink ("keep Ink deep enough to read against Paper"), so deriving
 * from it is self-maintaining in either direction. The stored column stays, so a curator who wants a
 * different on-dark colour can still set one — see `brandPaletteAdvisories` for the check that tells
 * them when the pair they chose does not work.
 */
export function derivedBrandButtonText(colors) {
    return colors.light;
}
/**
 * The fill of the filled primary action buttons when nobody has chosen one: INK, which is what those
 * buttons have always been filled with. Deriving it (rather than storing a literal) keeps an unticked
 * theme's buttons following its Ink exactly as they did before the colour existed. The stored column
 * stays, so a brand whose buttons are their main colour rather than their text colour (John Deere's
 * green) can set one — see `brandPaletteAdvisories` for the check on the label that sits on it.
 */
export function derivedBrandButton(colors) {
    return colors.dark;
}
/**
 * The primary button's fill while the pointer is on it or it is engaged.
 *
 * Kombi's own button swaps Ink for Accent on hover, so a theme with no chosen button colour keeps doing
 * exactly that. A CHOSEN button colour is already the brand's main colour, and swapping it for Accent would
 * be a jump to an unrelated colour (or, for a brand whose button IS its Accent, no change at all), so the
 * hover is a darker step of the button instead: 25% toward Ink. Computed here rather than in CSS because
 * the editor's sample and the live site must agree on the hex, and a `color-mix()` in a rule is banned.
 */
export function derivedBrandButtonHover(colors) {
    if (sameColour(colors.button, colors.dark))
        return colors.accent;
    const from = brandHexChannels(colors.button);
    const toward = brandHexChannels(colors.dark);
    // Not a colour this can mix (the API rejects these; a hand-edited local snapshot might not): keep the
    // button as it is rather than invent a fill.
    if (from === null || toward === null)
        return colors.button;
    const mixed = from.map((channel, i) => Math.round(channel * 0.75 + toward[i] * 0.25));
    return `#${mixed.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}
/**
 * The header bar's own fill when nobody has given it one: PAPER, so the bar is part of the page.
 *
 * The bar is the one surface a brand usually wants to own separately — a coloured band with light text
 * above an otherwise light site is the commonest request a white-label theme gets. Keeping it a stored
 * colour of its own (rather than "the header is always Accent") is what lets a theme have a coloured
 * header without that colour also becoming its buttons and its links.
 */
export function derivedBrandHeader(colors) {
    return colors.light;
}
/**
 * The header bar's text when nobody has chosen one: whichever of Ink and Paper reads better on the bar.
 *
 * Both are already in the palette and already contracted to contrast with each other, so one of them is
 * always a safe answer — a dark bar gets Paper, a light bar gets Ink, with no curator judgement and
 * nothing to keep in step by hand.
 */
export function derivedBrandHeaderText(colors) {
    const onInk = brandContrastRatio(colors.dark, colors.header);
    const onPaper = brandContrastRatio(colors.light, colors.header);
    if (onInk === null || onPaper === null)
        return colors.dark;
    return onPaper > onInk ? colors.light : colors.dark;
}
/**
 * What a focus ring and the unread-bell tint use INSIDE the header bar.
 *
 * Kombi's rule is that focus is always Accent, and it holds because Accent is contracted to read against
 * Paper. A bar filled with something else voids that guarantee — and the likeliest coloured header is the
 * brand's own Accent, which would make every focus ring in the bar invisible. So the rule is stated more
 * precisely: focus is Accent wherever Accent can be seen, and the surface's own foreground where it
 * cannot. 3:1 is the WCAG floor for a non-text indicator, which is what a ring is.
 *
 * Derived here rather than in CSS because CSS cannot ask "can this colour be seen on that one", and a
 * curator should not have to answer a question with one correct answer.
 */
export const BRAND_MIN_INDICATOR_CONTRAST = 3;
export function derivedBrandHeaderSignal(colors) {
    const onHeader = brandContrastRatio(colors.accent, colors.header);
    if (onHeader === null || onHeader >= BRAND_MIN_INDICATOR_CONTRAST)
        return colors.accent;
    return colors.headerText;
}
/** Case-insensitive colour equality — a stored #hex and a picker's output differ only in case. */
function sameColour(a, b) {
    return a.toLowerCase() === b.toLowerCase();
}
/** One 0-255 channel triple, or null when the value is not a colour this can measure. */
function brandHexChannels(hex) {
    if (!isBrandHexColor(hex))
        return null;
    const digits = hex.slice(1);
    // Alpha is IGNORED rather than composited: what a translucent brand colour ends up over depends on
    // the page, so measuring it here would report a contrast the curator never sees.
    const pairs = digits.length <= 4
        ? [...digits].slice(0, 3).map((digit) => digit + digit)
        : [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 6)];
    const [r, g, b] = pairs.map((pair) => Number.parseInt(pair, 16));
    return [r, g, b];
}
/** WCAG relative luminance (0 = black, 1 = white). Null for a value that is not a #hex. */
export function brandRelativeLuminance(hex) {
    const channels = brandHexChannels(hex);
    if (channels === null)
        return null;
    const [r, g, b] = channels.map((channel) => {
        const proportion = channel / 255;
        return proportion <= 0.04045 ? proportion / 12.92 : ((proportion + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG contrast ratio between two #hex colours, 1 (identical) to 21 (black on white). */
export function brandContrastRatio(a, b) {
    const first = brandRelativeLuminance(a);
    const second = brandRelativeLuminance(b);
    if (first === null || second === null)
        return null;
    return (Math.max(first, second) + 0.05) / (Math.min(first, second) + 0.05);
}
/** WCAG AA for normal-size text. Every pair below carries real body or label copy. */
export const BRAND_MIN_CONTRAST = 4.5;
export function brandPaletteAdvisories(colors) {
    const advisories = [];
    // While the on-dark colour is the derived one, the field for it is behind a switch — so the advice
    // names PAPER, which is the control the curator actually has in front of them.
    const onDark = colors.buttonText === derivedBrandButtonText(colors) ? 'Paper' : 'On-dark text';
    const buttonIsInk = sameColour(colors.button, colors.dark);
    const pairs = [
        {
            id: 'ink-on-paper',
            on: colors.dark,
            onLabel: 'Ink',
            over: colors.light,
            overLabel: 'Paper',
            what: 'Body text on the page',
        },
        {
            id: 'ink-on-surface',
            on: colors.dark,
            onLabel: 'Ink',
            over: colors.surface,
            overLabel: 'Surface',
            what: 'Text on panels and cards',
        },
        // Ink is still a fill with a label on it once a theme chooses its own Button — the covers and marks
        // — so the pair is always checked; only what it is CALLED changes, because once Button is chosen a
        // primary button is no longer Ink and saying so would send the curator to the wrong swatch.
        {
            id: 'on-dark-on-ink',
            on: colors.buttonText,
            onLabel: onDark,
            over: colors.dark,
            overLabel: 'Ink',
            what: buttonIsInk ? 'The label on a primary button' : 'The copy on a dark cover',
        },
        // Accent is the engaged fill only while the button is Ink (derivedBrandButtonHover). With a chosen
        // Button it is a darker step of that colour instead, checked below as its own pair; Accent itself is
        // still filled by the solid feature panels, so it keeps a description that is true either way.
        {
            id: 'on-dark-on-accent',
            on: colors.buttonText,
            onLabel: onDark,
            over: colors.accent,
            overLabel: 'Accent',
            what: buttonIsInk
                ? 'The label on a button while the pointer is on it'
                : 'Text on an Accent fill',
        },
        // A chosen Button colour is a third fill the label sits on, and its hover step a fourth. Skipped where
        // it IS Ink or Accent: those pairs are already checked above, and a second message about the same
        // pair would only repeat it. The hover step sits between Button and Ink, so it is usually at least as
        // readable as Button — but not always (a mid-tone Ink), and it is the fill under every pointer.
        ...(buttonIsInk || sameColour(colors.button, colors.accent)
            ? []
            : [
                {
                    id: 'on-dark-on-button',
                    on: colors.buttonText,
                    onLabel: onDark,
                    over: colors.button,
                    overLabel: 'Button',
                    what: 'The label on a primary button',
                },
            ]),
        ...(buttonIsInk
            ? []
            : [
                {
                    id: 'on-dark-on-button-hover',
                    on: colors.buttonText,
                    onLabel: onDark,
                    over: derivedBrandButtonHover(colors),
                    overLabel: 'Button',
                    what: 'The label on a button while the pointer is on it',
                },
            ]),
        {
            id: 'ink-on-highlight',
            on: colors.dark,
            onLabel: 'Ink',
            over: colors.highlight,
            overLabel: 'Highlight',
            what: 'The label on a selected row or chip',
        },
        {
            id: 'header-text-on-header',
            on: colors.headerText,
            onLabel: 'Header text',
            over: colors.header,
            overLabel: 'Header',
            what: 'The navigation and name in the header',
            // A DERIVED header text is already the better of Ink and Paper against the bar, so if it still
            // fails, neither of them reads on it and there is nothing to move but the bar itself. That is a
            // mid-tone header, and the fix is to commit to a light or a dark one.
            ...(colors.headerText === derivedBrandHeaderText(colors)
                ? { fix: 'Make Header clearly darker, or clearly lighter.' }
                : {}),
        },
    ];
    for (const pair of pairs) {
        const ratio = brandContrastRatio(pair.on, pair.over);
        if (ratio === null || ratio >= BRAND_MIN_CONTRAST)
            continue;
        // Separating a failing pair means pushing the darker one down or the lighter one up. Naming them the
        // wrong way round would send a curator the way that makes it worse.
        const onLuminance = brandRelativeLuminance(pair.on);
        const overLuminance = brandRelativeLuminance(pair.over);
        const [darker, lighter] = onLuminance <= overLuminance
            ? [pair.onLabel, pair.overLabel]
            : [pair.overLabel, pair.onLabel];
        // Only offer a direction that exists: "lighten Paper" is not advice when Paper is already white.
        let fix = `Darken ${darker}, or lighten ${lighter}.`;
        if (Math.max(onLuminance, overLuminance) > 0.99)
            fix = `Darken ${darker}.`;
        else if (Math.min(onLuminance, overLuminance) < 0.01)
            fix = `Lighten ${lighter}.`;
        if (pair.fix !== undefined)
            fix = pair.fix;
        advisories.push({ id: pair.id, ratio, message: `${pair.what} is hard to read. ${fix}` });
    }
    const surface = brandRelativeLuminance(colors.surface);
    const paper = brandRelativeLuminance(colors.light);
    if (surface !== null && paper !== null) {
        if (Math.abs(surface - paper) < 0.01) {
            advisories.push({
                id: 'surface-matches-paper',
                message: 'Panels and cards will not show against the page. Make Surface a shade lighter than Paper.',
            });
        }
        else if (surface < paper) {
            // Kombi's hero reads as pale only because the content band below it is LIGHTER; a darker Surface
            // does not fail, it inverts every page that has a hero. Worth saying out loud, not refusing.
            advisories.push({
                id: 'surface-darker-than-paper',
                message: 'Every hero will read as the light part of its page, which is the wrong way round. Make Surface lighter than Paper.',
            });
        }
    }
    return advisories;
}
/**
 * The corner shape of every action button. A closed set of keywords rather than a free radius, so a
 * curator picks a look instead of typing CSS, and nothing user-typed ever reaches a stylesheet. Mirrored
 * by the CHECK constraint in migration 0061. `square` is Kombi's own (its buttons have no radius), so it
 * is what an omitted value, and every theme that predates the setting, resolves to.
 */
export const BRAND_BUTTON_SHAPES = ['square', 'rounded', 'pill'];
export const DEFAULT_BRAND_BUTTON_SHAPE = 'square';
export function isBrandButtonShape(v) {
    return typeof v === 'string' && BRAND_BUTTON_SHAPES.includes(v);
}
/** The `border-radius` a button shape stands for — the one value the runtime publishes as
 * `--brand-button-radius`, so the editor's sample buttons and the live site cannot drift apart. */
export function brandButtonRadius(shape) {
    switch (shape) {
        case 'square':
            return '0px';
        case 'rounded':
            return '6px';
        case 'pill':
            return '999px';
    }
}
/**
 * How the What's on and Made in the Community panels are filled. `tint` is today's 7% Accent wash over
 * Paper; `solid` fills them with Accent itself, for a brand whose wash would come out a colour that is
 * not theirs (John Deere's pale wash is a mint). Mirrored by the CHECK constraint in migration 0061.
 */
export const BRAND_PANEL_STYLES = ['tint', 'solid'];
export const DEFAULT_BRAND_PANEL_STYLE = 'tint';
export function isBrandPanelStyle(v) {
    return typeof v === 'string' && BRAND_PANEL_STYLES.includes(v);
}
/** Length caps for the two free-text fields. Mirror the CHECK constraints in migration 0052. */
export const BRAND_NAME_MAX = 60; // the preset's name in the drawer
export const BRAND_WORDMARK_MAX = 40; // the product name that replaces "Kombi"
export const BRAND_COMPANY_MAX = 80; // organisation name used in theme-aware editorial copy
/**
 * Validate the non-binary half of a preset (the create/edit form). Returns null when acceptable, or a
 * short reason token the route maps to a 400 detail. Authoritative and environment-free so a route and
 * a test share ONE gate. Assets are validated separately, at upload, by the blob route.
 */
export function validateBrandPresetInput(input) {
    if (typeof input.name !== 'string' || input.name.trim().length === 0)
        return 'name.required';
    if (input.name.trim().length > BRAND_NAME_MAX)
        return 'name.length';
    if (typeof input.wordmark !== 'string' || input.wordmark.trim().length === 0) {
        return 'wordmark.required';
    }
    if (input.wordmark.trim().length > BRAND_WORDMARK_MAX)
        return 'wordmark.length';
    if (typeof input.company !== 'string' || input.company.trim().length === 0)
        return 'company.required';
    if (input.company.trim().length > BRAND_COMPANY_MAX)
        return 'company.length';
    const c = input.colors ?? {};
    if (!isBrandHexColor(c.accent))
        return 'colors.accent';
    if (!isBrandHexColor(c.dark))
        return 'colors.dark';
    if (!isBrandHexColor(c.light))
        return 'colors.light';
    if (!isBrandHexColor(c.surface))
        return 'colors.surface';
    if (!isBrandHexColor(c.highlight))
        return 'colors.highlight';
    if (!isBrandHexColor(c.buttonText))
        return 'colors.buttonText';
    if (!isBrandHexColor(c.header))
        return 'colors.header';
    if (!isBrandHexColor(c.headerText))
        return 'colors.headerText';
    // Optional: absent is fine, an old client omits it. Present-but-not-a-hex is rejected (null included).
    if (c.button !== undefined && !isBrandHexColor(c.button))
        return 'colors.button';
    if (typeof input.disableHomeScrollAnimation !== 'boolean') {
        return 'disableHomeScrollAnimation.invalid';
    }
    if (typeof input.useSeparateHeadingFont !== 'boolean') {
        return 'useSeparateHeadingFont.invalid';
    }
    // Optional: absent (undefined) is fine, a client that predates these still sends the old payload. Only
    // a value that is present and not in the set is rejected — null included, which is not "omitted".
    if (input.buttonShape !== undefined && !isBrandButtonShape(input.buttonShape)) {
        return 'buttonShape.invalid';
    }
    if (input.panelStyle !== undefined && !isBrandPanelStyle(input.panelStyle)) {
        return 'panelStyle.invalid';
    }
    return null;
}
//# sourceMappingURL=brand.js.map